import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, X, Trash2, Users, Archive, CheckCircle2, Crown, Sparkles, Timer, Repeat, Bell, ExternalLink, Mail, Share2, Copy, Lock, Calendar as CalendarIcon, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { API, COLUMN_COLORS } from "@/utils/constants";
import { Avatar } from "@/components/shared";

export const ColumnModal = ({ isOpen, onClose, column, onSave }) => {
  const [formData, setFormData] = useState({ name: "", color: COLUMN_COLORS[0], is_done_column: false });
  useEffect(() => { if (column) setFormData({ name: column.name || "", color: column.color || COLUMN_COLORS[0], is_done_column: column.is_done_column || false }); else setFormData({ name: "", color: COLUMN_COLORS[Math.floor(Math.random() * COLUMN_COLORS.length)], is_done_column: false }); }, [column, isOpen]);
  const handleSubmit = (e) => { e.preventDefault(); if (!formData.name.trim()) { toast.error("Name required"); return; } onSave(formData, column?.id); };
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[400px]"><DialogHeader><DialogTitle>{column ? "Edit Column" : "Add Column"}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><label className="text-sm font-medium mb-1.5 block">Name</label><Input placeholder="e.g., In Review" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} /></div>
          <div><label className="text-sm font-medium mb-1.5 block">Color</label><div className="flex flex-wrap gap-2">{COLUMN_COLORS.map(c => <button key={c} type="button" className={`w-8 h-8 rounded-full transition-all shadow-sm ${formData.color === c ? 'ring-2 ring-offset-2 ring-primary scale-110' : 'hover:scale-105'}`} style={{ backgroundColor: c }} onClick={() => setFormData({ ...formData, color: c })} />)}</div></div>
          <div className="flex items-center gap-2"><Checkbox checked={formData.is_done_column} onCheckedChange={(checked) => setFormData({ ...formData, is_done_column: checked })} /><label className="text-sm">Mark complete when moved here</label></div>
          <DialogFooter><Button type="button" variant="outline" onClick={onClose}>Cancel</Button><Button type="submit">{column ? "Update" : "Create"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const ContactModal = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({ name: "", email: "" });
  const handleSubmit = (e) => { e.preventDefault(); if (!formData.name.trim() || !formData.email.trim()) { toast.error("Name and email required"); return; } onSave(formData); setFormData({ name: "", email: "" }); };
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[400px]"><DialogHeader><DialogTitle>Add Contact</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><label className="text-sm font-medium mb-1.5 block">Name</label><Input placeholder="John Doe" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} /></div>
          <div><label className="text-sm font-medium mb-1.5 block">Email</label><Input type="email" placeholder="john@example.com" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} /></div>
          <DialogFooter><Button type="button" variant="outline" onClick={onClose}>Cancel</Button><Button type="submit">Add Contact</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const ContactsModal = ({ isOpen, onClose, contacts, onAddContact, onDeleteContact }) => {
  const [showAdd, setShowAdd] = useState(false);
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-primary" />Contacts</DialogTitle></DialogHeader>
        <div className="flex justify-end mb-2"><Button size="sm" onClick={() => setShowAdd(true)}><Plus className="h-4 w-4 mr-1" />Add</Button></div>
        <ScrollArea className="max-h-[50vh]">
          {contacts.length === 0 ? <div className="text-center py-8 text-muted-foreground">No contacts</div> : (
            <div className="space-y-2">{contacts.map(c => (
              <div key={c.id} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg group">
                <Avatar name={c.name} color={c.avatar_color} size="md" />
                <div className="flex-1"><div className="font-medium text-sm">{c.name}</div><div className="text-xs text-muted-foreground flex items-center gap-1"><Mail className="h-3 w-3" />{c.email}</div></div>
                <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100" onClick={() => onDeleteContact(c.id)}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}</div>
          )}
        </ScrollArea>
        <ContactModal isOpen={showAdd} onClose={() => setShowAdd(false)} onSave={(d) => { onAddContact(d); setShowAdd(false); }} />
      </DialogContent>
    </Dialog>
  );
};

export const ArchiveModal = ({ isOpen, onClose, tasks, onRestore, onDelete }) => (
  <Dialog open={isOpen} onOpenChange={onClose}>
    <DialogContent className="sm:max-w-[600px] max-h-[80vh]">
      <DialogHeader><DialogTitle className="flex items-center gap-2"><Archive className="h-5 w-5" />Archive</DialogTitle></DialogHeader>
      <ScrollArea className="max-h-[50vh]">
        {tasks.length === 0 ? <div className="text-center py-8 text-muted-foreground">Empty</div> : (
          <div className="space-y-2">{tasks.map(t => (
            <div key={t.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div><h4 className="font-medium text-sm">{t.title}</h4></div>
              <div className="flex gap-2"><Button size="sm" variant="outline" onClick={() => onRestore(t)}>Restore</Button><Button size="sm" variant="destructive" onClick={() => onDelete(t.id)}>Delete</Button></div>
            </div>
          ))}</div>
        )}
      </ScrollArea>
    </DialogContent>
  </Dialog>
);

export const UpgradeModal = ({ isOpen, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("pro_monthly");
  const handleUpgrade = async () => { setIsLoading(true); try { const r = await axios.post(`${API}/payments/checkout`, { plan_id: selectedPlan, origin_url: window.location.origin }); window.location.href = r.data.url; } catch { toast.error("Failed"); setIsLoading(false); } };
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Crown className="h-6 w-6 text-amber-500" />Upgrade to Pro</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-2">
            <button onClick={() => setSelectedPlan("pro_monthly")} className={`p-3 rounded-xl border-2 transition-all ${selectedPlan === "pro_monthly" ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`} data-testid="plan-pro-monthly"><div className="text-xl font-bold">$7.99</div><div className="text-xs text-muted-foreground">Pro /mo</div></button>
            <button onClick={() => setSelectedPlan("pro_yearly")} className={`p-3 rounded-xl border-2 relative ${selectedPlan === "pro_yearly" ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`} data-testid="plan-pro-yearly"><Badge className="absolute -top-2 -right-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-[9px] px-1.5">-17%</Badge><div className="text-xl font-bold">$79</div><div className="text-xs text-muted-foreground">Pro /yr</div></button>
            <button onClick={() => setSelectedPlan("bundle_monthly")} className={`p-3 rounded-xl border-2 relative ${selectedPlan === "bundle_monthly" ? "border-violet-500 bg-violet-500/5" : "border-border hover:border-violet-400/50"}`} data-testid="plan-bundle-monthly"><Badge className="absolute -top-2 -right-2 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-[9px] px-1.5">+CRM</Badge><div className="text-xl font-bold">$12.99</div><div className="text-xs text-muted-foreground">Bundle /mo</div></button>
          </div>
          {selectedPlan === "bundle_monthly" && <p className="text-xs text-violet-600 dark:text-violet-400 font-medium -mt-1" data-testid="bundle-hint">Business Bundle = everything in Pro + the full CRM (contacts, companies & deals pipeline)</p>}
          <div className="space-y-3 py-2">
            <div className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-emerald-500" /><span>Unlimited columns & tasks</span></div>
            <div className="flex items-center gap-3"><Sparkles className="h-5 w-5 text-amber-500" /><span><strong>AI Assistant</strong></span></div>
            <div className="flex items-center gap-3"><Timer className="h-5 w-5 text-blue-500" /><span>Time tracking</span></div>
            <div className="flex items-center gap-3"><Repeat className="h-5 w-5 text-purple-500" /><span>Recurring tasks & automation rules</span></div>
            <div className="flex items-center gap-3"><Bell className="h-5 w-5 text-pink-500" /><span>Calendar sync (Google, Outlook, Apple)</span></div>
            <div className="flex items-center gap-3"><MessageSquare className="h-5 w-5 text-sky-500" /><span>Team chat & direct messages</span></div>
            <div className="flex items-center gap-3"><Crown className="h-5 w-5 text-violet-500" /><span><strong>CRM suite</strong> — contacts, companies & deals pipeline <Badge variant="secondary" className="ml-1 text-[9px]">Bundle only</Badge></span></div>
          </div>
          <Button className="w-full" size="lg" onClick={handleUpgrade} disabled={isLoading} data-testid="upgrade-btn">{isLoading ? "Processing..." : "Upgrade Now"}<ExternalLink className="h-4 w-4 ml-2" /></Button>
          <Button variant="outline" className="w-full" size="lg" onClick={async () => { try { await axios.post(`${API}/trial/start`); toast.success("14-day Pro trial started!"); onClose(); window.location.reload(); } catch (e) { toast.error(e.response?.data?.detail || "Failed to start trial"); } }} data-testid="start-trial-btn">Start 14-Day Free Trial</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const PaymentSuccess = ({ onClose }) => {
  const [status, setStatus] = useState("checking");
  useEffect(() => {
    const check = async () => {
      const p = new URLSearchParams(window.location.search);
      const sid = p.get('session_id');
      if (!sid) { setStatus("error"); return; }
      let attempts = 0;
      const poll = async () => {
        try {
          const r = await axios.get(`${API}/payments/status/${sid}`);
          if (r.data.payment_status === "paid") { setStatus("success"); toast.success("Payment successful!"); setTimeout(() => { window.history.replaceState({}, '', '/'); onClose(); }, 2000); }
          else if (attempts++ < 5) setTimeout(poll, 2000);
          else setStatus("pending");
        } catch { setStatus("error"); }
      };
      poll();
    };
    check();
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-background z-50 flex items-center justify-center">
      <div className="text-center">
        {status === "checking" && <><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" /><h2 className="text-xl font-semibold">Verifying...</h2></>}
        {status === "success" && <><CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto mb-4" /><h2 className="text-2xl font-bold mb-2">Welcome to Pro!</h2></>}
        {status === "error" && <><div className="h-16 w-16 text-red-500 mx-auto mb-4 flex items-center justify-center"><X className="h-12 w-12" /></div><h2 className="text-xl font-semibold mb-2">Error</h2><Button onClick={() => { window.history.replaceState({}, '', '/'); onClose(); }}>Return</Button></>}
      </div>
    </div>
  );
};

export const ShareModal = ({ isOpen, onClose, entityType, entityId, onShare }) => {
  const [password, setPassword] = useState("");
  const [expiresInDays, setExpiresInDays] = useState("none");
  const [isLoading, setIsLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [activeLinks, setActiveLinks] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(false);

  useEffect(() => {
    if (isOpen && entityId) {
      setShareUrl(null);
      setPassword("");
      setExpiresInDays("none");
      loadLinks();
    }
  }, [isOpen, entityId]);

  const loadLinks = async () => {
    setLoadingLinks(true);
    try {
      const r = await axios.get(`${API}/share/links/${entityType}/${entityId}`);
      setActiveLinks(r.data);
    } catch {}
    setLoadingLinks(false);
  };

  const handleCreate = async () => {
    setIsLoading(true);
    try {
      const body = {};
      if (password.trim()) body.password = password.trim();
      if (expiresInDays !== "none") body.expires_in_days = parseInt(expiresInDays);

      const r = await axios.post(`${API}/share/${entityType}/${entityId}`, body);
      const url = `${window.location.origin}/shared/${r.data.share_token}`;
      setShareUrl(url);
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied to clipboard!");
      loadLinks();
    } catch { toast.error("Failed to create share link"); }
    setIsLoading(false);
  };

  const handleRevoke = async (shareId) => {
    try {
      await axios.delete(`${API}/share/${shareId}`);
      toast.success("Link revoked");
      loadLinks();
    } catch { toast.error("Failed to revoke"); }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/shared/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Copied!");
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5 text-primary" />Share {entityType === "project" ? "Project" : "Task"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1.5 block flex items-center gap-1.5">
              <Lock className="h-3.5 w-3.5" />Password (optional)
            </label>
            <Input
              type="password"
              placeholder="Leave empty for public access"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="share-modal-password"
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-1.5 block flex items-center gap-1.5">
              <CalendarIcon className="h-3.5 w-3.5" />Expiry (optional)
            </label>
            <Select value={expiresInDays} onValueChange={setExpiresInDays}>
              <SelectTrigger data-testid="share-modal-expiry"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Never</SelectItem>
                <SelectItem value="1">1 day</SelectItem>
                <SelectItem value="7">7 days</SelectItem>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="90">90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button onClick={handleCreate} disabled={isLoading} className="w-full" data-testid="share-modal-create">
            {isLoading ? "Creating..." : "Create Share Link"}
          </Button>

          {shareUrl && (
            <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
              <div className="flex items-center gap-2">
                <Input value={shareUrl} readOnly className="text-xs" data-testid="share-modal-url" />
                <Button variant="outline" size="icon" onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Copied!"); }}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {activeLinks.length > 0 && (
            <div>
              <h4 className="text-sm font-medium mb-2">Active Links ({activeLinks.length})</h4>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {activeLinks.map(link => (
                  <div key={link.id} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg text-xs">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className="truncate font-mono">{link.share_token}</span>
                      {link.has_password && <Lock className="h-3 w-3 text-amber-500 shrink-0" />}
                      {link.expires_at && <CalendarIcon className="h-3 w-3 text-blue-500 shrink-0" />}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyLink(link.share_token)}>
                        <Copy className="h-3 w-3" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => handleRevoke(link.id)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

